import  { AxiosRequestConfig } from 'axios';
import { performTokenRefresh } from './refreshFlow.ts';
import axiosInstance from '../axios.js';

declare global {
  interface Navigator {
    locks?: {
      request<T>(name: string, callback: () => Promise<T> | T): Promise<T>;
    };
  }
}

// Backend API base URL. When frontend and backend are on different sites, this
// must point to the backend origin (e.g. https://your-backend.example.com or http://localhost:8080).
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// For cookie-based auth we do NOT send an Authorization header.
// Backend will read access_token from cookies instead.
const authorizationHeadersInternal = (): Record<string, string> => {
  return {};
};

export const authorizationHeaders = authorizationHeadersInternal;

const defaultAxiosConfig = (): AxiosRequestConfig => {
  return {
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
};

const authorizedAxiosConfig = (): AxiosRequestConfig => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...authorizationHeadersInternal(),
  };

  return {
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers,
  };
};

// This method executes a request created within a function provided as `fn`.
// If the request fails with 401, it will try to refresh cookies via /api/refresh
// (under a cross-tab lock in refreshFlow) and then retry.
export const refreshTokenAndRetry = <Type>(
  fn: () => Promise<Type>,
  retryCount: number = 2,
  err: Error | undefined = undefined
): Promise<Type> => {
  return fn().catch(async (error: any) => {
    if (retryCount <= 0) throw error || err;

    const status = error?.status ?? error?.response?.status;
    if (status !== 401) throw error || err;

    try {
      await performTokenRefresh();
    } catch (e) {
      return Promise.reject(e || error || err);
    }

    // The lock is released and token was refreshed by this or another tab.
    return refreshTokenAndRetry(fn, retryCount - 1, error || err);
  });
};

const processError = (error: any) => {
  // Hook for custom error handling (toasts, message mapping, etc.)
  throw error;
};

const notAuthorizedApiRequest = (
  config: AxiosRequestConfig = {}
): Promise<any> => {
  return axiosInstance({
    ...defaultAxiosConfig(),
    ...config,
  })
    .then((response) => response.data)
    .catch(processError);
};

const authorizedApiRequest = (
  config: AxiosRequestConfig = {}
): Promise<any> => {
  return refreshTokenAndRetry(() => {
    return axiosInstance({
      ...authorizedAxiosConfig(),
      ...config,
      withCredentials: true,
    })
      .then((response) => response.data)
      .catch(processError);
  });
};

const apiGet = (url: string, params?: object): Promise<any> => {
  return authorizedApiRequest({ method: 'GET', url, params });
};

const apiPost = (
  url: string,
  data: object,
  onUploadProgress?: (progressEvent: any) => void
): Promise<any> => {
  return authorizedApiRequest({ method: 'POST', url, data, onUploadProgress });
};

/** POST JSON body, binary response (exports, downloads). Same auth + refresh retry as `apiPost`. */
const apiPostBlob = (url: string, data: object): Promise<Blob> => {
  return authorizedApiRequest({
    method: 'POST',
    url,
    data,
    responseType: 'blob',
  });
};

const apiPut = (url: string, data: object): Promise<any> => {
  return authorizedApiRequest({ method: 'PUT', url, data });
};

const apiPatch = (url: string, data: object): Promise<any> => {
  return authorizedApiRequest({ method: 'PATCH', url, data });
};

const apiDelete = (url: string, data?: object): Promise<any> => {
  return authorizedApiRequest({ method: 'DELETE', url, data });
};

export {
  authorizedApiRequest,
  notAuthorizedApiRequest,
  apiGet,
  apiPatch,
  apiPost,
  apiPostBlob,
  apiPut,
  apiDelete,
};
